/**
 * The Request summary — one printable document per Request ID (v68 `requestPdf`).
 *
 * <p>What the button is FOR: the person who took the call needs to hand someone
 * the whole request — the header, every service on it, and what has happened to
 * it — without that person having to open the form and click through four
 * sections. It is produced in the browser and printed through it, so it needs
 * nothing installed and works with no connection.</p>
 *
 * <p>It is NOT the Service Task Report. That is raised per service from the
 * completed survey (§8) and is the client-facing record of the work; this is a
 * summary of the REQUEST record. The footer says so, because the two are easy to
 * confuse and only one of them is evidence.</p>
 *
 * <p>The document is built from the SAME reads the form uses — `fetchServiceTasks`
 * for the services, `resolveFields` / `resolveReport` / `resolveJobRule` for what
 * each one shows, and the shared `/audit/trail` endpoint for the history
 * (standing rule 8). Nothing here re-derives a rule: a summary that disagreed
 * with the form would be worse than no summary at all.</p>
 *
 * <p>Scoped to the Client Request module (§18.2). `buildRequestSummaryHtml` is
 * pure and exported so it can be tested without a browser window.</p>
 */
import { fmtLocalDate, fmtLocalDateTime, nowClientWire } from '../../utils/datetime';
import {
  AUDIT_FORM_NAME, fetchAuditTrail, fetchServiceDefinitions, fetchServiceTasks,
  resolveFields, resolveJobRule, resolveReport,
  type AuditTrailRow, type ServiceCategoryDef, type ServiceTaskRow,
} from './clientRequestApi';

/** The request header, as the FORM currently shows it — names, not Oids. Taken
 *  from the page's own state rather than re-fetched, so the summary matches what
 *  the user is looking at (including edits they have not saved yet). */
export interface RequestSummaryInput {
  requestNo:   string;
  status:      string;
  client:      string;
  contact:     string;
  project:     string;
  priority:    string;
  /** `YYYY-MM-DD` — a calendar day, kept as a string (see `fmtCalendarDay`). */
  calledOn:    string;
  heardAbout:  string;
  quoteNo:     string;
  owner:       string;
  /** Rich text (HTML) from the description editor. */
  description: string;
}

/** HTML-escape a value for insertion as TEXT. */
function esc(v: string): string {
  return v
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

/** An em dash for anything the record does not hold, so a blank cell always
 *  means "not recorded" rather than "the report lost it". */
function val(v: string | null | undefined): string {
  return v && v.trim() ? esc(v) : '<span class="na">&mdash;</span>';
}

/**
 * A calendar day (`YYYY-MM-DD`) as `MM/DD/YYYY`, reordered from the STRING.
 *
 * <p>Deliberately not routed through a `Date`: "called on the 9th" becomes the
 * 8th for anyone west of the server the moment a plain day is parsed as an
 * instant — the same reasoning that keeps `calledOn` a string all the way to the
 * wire. No locale call is involved (§18.15 C2); this reorders three known parts.
 * Instants — audit timestamps, report approvals — use the shared `fmtLocal*`
 * helpers, which is what they are for.</p>
 */
function fmtCalendarDay(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim());
  return m ? `${m[2]}/${m[3]}/${m[1]}` : iso;
}

/** `HH:mm:ss` → `HH:mm`. 24-hour, never AM/PM (§18.6). */
function fmtClock(t: string | null): string {
  return t ? t.slice(0, 5) : '';
}

/**
 * Strip anything executable out of the stored rich text.
 *
 * <p>The description is the lab's own content and already renders in the editor,
 * but this document is written into a NEW window: a stray `<script>` or an
 * `onerror=` pasted in from an email would run there with nothing around it to
 * notice. The summary is a printout, so it needs none of those tags to be
 * faithful, and dropping them costs the reader nothing.</p>
 */
function safeRichText(html: string): string {
  return html
    .replace(/<\s*(script|iframe|object|embed|link|meta)\b[\s\S]*?<\s*\/\s*\1\s*>/gi, '')
    .replace(/<\s*(script|iframe|object|embed|link|meta)\b[^>]*\/?>/gi, '')
    .replace(/\son\w+\s*=\s*"[^"]*"/gi, '')
    .replace(/\son\w+\s*=\s*'[^']*'/gi, '')
    .replace(/\son\w+\s*=\s*[^\s>]+/gi, '')
    .replace(/javascript:/gi, '');
}

/** Rich text is empty when it has no TEXT, whatever markup wraps it — an editor
 *  left untouched still holds "<p><br></p>", which is not content. */
function htmlIsBlank(html: string): boolean {
  return !html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
}

/** A label/value table — the shape every block on the page uses. Rows whose
 *  value is empty still render, because "not recorded" is information. */
function kv(rows: [string, string | null | undefined][]): string {
  return '<table>' + rows
    .map(([k, v]) => `<tr><td class="k">${esc(k)}</td><td>${val(v)}</td></tr>`)
    .join('') + '</table>';
}

/** The print stylesheet. Its own document, so it cannot read our CSS variables —
 *  the palette is restated here from the same tokens (`--ai-ink` #1f2430,
 *  `--ai-muted` #6b7280) so the printout looks like the app it came from. */
function summaryCss(): string {
  return '@page{size:Letter;margin:16mm}'
    + 'body{margin:0;font-family:Tahoma,Verdana,"Segoe UI",sans-serif;font-size:10.5pt;'
    + 'line-height:1.5;color:#1f2430;background:#fff}'
    + '.wrap{max-width:190mm;margin:0 auto;padding:10mm}'
    + 'h1{font-size:20pt;margin:0 0 1mm;letter-spacing:-.4px}'
    + 'h2{font-size:12pt;margin:9mm 0 2mm;padding-bottom:1.5mm;'
    + 'border-bottom:2px solid #1f2430;break-after:avoid}'
    + 'h3{font-size:10.5pt;margin:5mm 0 1.5mm;break-after:avoid}'
    + '.sub{color:#6b7280;margin:0 0 6mm}'
    + '.pill{display:inline-block;padding:1mm 3mm;border-radius:3mm;font-size:8.5pt;'
    + 'font-weight:700;background:#f4f5f7;border:1px solid #e3e6ea}'
    + 'table{width:100%;border-collapse:collapse;margin:0 0 3mm;font-size:9.5pt}'
    + 'th{background:#fafbfc;text-align:left;font-size:8pt;letter-spacing:.4px;'
    + 'text-transform:uppercase;color:#6b7280;padding:2mm;border:1px solid #e3e6ea}'
    + 'td{padding:2mm;border:1px solid #e3e6ea;vertical-align:top}'
    + 'td.k{width:46mm;color:#6b7280;background:#fafbfc}'
    + '.svc{break-inside:avoid;margin:0 0 4mm}'
    + '.na{color:#9aa1ad}'
    + '.desc{border:1px solid #e3e6ea;padding:3mm 4mm;background:#fafbfc}'
    + '.desc p,.desc li{margin:0 0 1.5mm}'
    + '.foot{margin-top:8mm;padding-top:3mm;border-top:1px solid #e3e6ea;'
    + 'font-size:8.5pt;color:#6b7280}';
}

/** "When" as one phrase: the agreed day, plus the time only when one was agreed. */
function whenText(t: ServiceTaskRow): string {
  if (!t.scheduledDate) return '';
  const time = fmtClock(t.scheduledTime);
  return fmtLocalDate(t.scheduledDate) + (time ? ` ${time}` : '');
}

/**
 * The whole document, as a string. Pure — every input is passed in, so the
 * summary can be asserted on in a unit test without opening a window.
 */
export function buildRequestSummaryHtml(
  req:   RequestSummaryInput,
  tasks: ServiceTaskRow[],
  defs:  ServiceCategoryDef[],
  audit: AuditTrailRow[],
): string {
  // A draft service has not been committed to the request, so it is not part of
  // the record this document summarises (v68 filters `!t.draft` the same way).
  const saved = tasks.filter(t => !t.isDraft);
  const catOf = (t: ServiceTaskRow) => defs.find(d => d.code === t.categoryCode);
  const svcName = (t: ServiceTaskRow) => {
    const name = catOf(t)?.name ?? t.categoryCode ?? '';
    return name + (t.serviceType ? ` · ${t.serviceType}` : '');
  };

  // The services as a table first — the thing anyone opens this to see.
  const svcTable = saved.length
    ? '<table><thead><tr><th>Service ID</th><th>Service</th><th>Where</th>'
      + '<th>When</th><th>Assigned to</th><th>Job</th></tr></thead><tbody>'
      + saved.map(t =>
        `<tr><td><b>${esc(t.serviceNo ?? '—')}</b></td>`
        + `<td>${esc(svcName(t))}</td>`
        + `<td>${val(t.siteAddress)}</td>`
        + `<td>${val(whenText(t))}</td>`
        + `<td>${t.assignedToName
          ? esc(t.assignedToName) : '<span class="na">Unassigned</span>'}</td>`
        + `<td>${val(t.jobNo)}</td></tr>`).join('')
      + '</tbody></table>'
    : '<p class="na">No services on this request yet.</p>';

  // Then each one in full, including whatever its Service Definition adds.
  const svcDetail = saved.map(t => {
    const cat = catOf(t);
    const rep = resolveReport(cat, t.serviceType);
    const answers = resolveFields(cat, t.serviceType, t.fields)
      .map(f => [f.caption, t.fields[f.id] ?? ''] as [string, string])
      .filter(([, v]) => v.trim());
    const jobRule = resolveJobRule(cat, t.serviceType, t.fields);

    const reportBlock = rep?.required
      ? kv([
        ['Deliverable', t.reportTemplate ?? rep.templates[0] ?? ''],
        ['Report status', t.reportVersion
          ? `v${t.reportVersion}` + (t.reportApprovedAt
            ? ` · approved ${fmtLocalDateTime(t.reportApprovedAt)}`
            : ' · awaiting approval')
          : `Raised on ${rep.generatedOn.toLowerCase()}`],
        ['Client-visible', rep.clientVisible ? 'Yes' : 'No'],
      ])
      : '';

    return `<div class="svc"><h3>${esc(t.serviceNo ?? 'Unsaved service')} · ${esc(svcName(t))}</h3>`
      + kv([
        ['Where', t.siteAddress],
        ['When', whenText(t)],
        ['Assigned to', t.assignedToName],
        ['Route', t.routeName],
        ['Laboratory job', t.jobNo || jobRule],
        ['Service instructions', t.instructions],
      ])
      + (answers.length ? kv(answers) : '')
      + reportBlock
      + '</div>';
  }).join('');

  const histTable = audit.length
    ? '<table><thead><tr><th>When</th><th>User</th><th>Change</th>'
      + '<th>From</th><th>To</th></tr></thead><tbody>'
      + audit.slice(0, 40).map(a =>
        `<tr><td>${val(a.createdDate ? fmtLocalDateTime(a.createdDate) : '')}</td>`
        + `<td>${val(a.createdBy)}</td>`
        + `<td>${val(a.propertyName ?? a.operationType)}</td>`
        + `<td>${val(a.oldValue)}</td><td>${val(a.newValue)}</td></tr>`).join('')
      + '</tbody></table>'
      + (audit.length > 40
        ? `<p class="na">Showing the 40 most recent of ${audit.length}.</p>` : '')
    : '<p class="na">Nothing recorded against this request yet.</p>';

  return '<!doctype html><html><head><meta charset="utf-8">'
    + `<title>${esc(req.requestNo)} · Request summary</title>`
    + `<style>${summaryCss()}</style></head><body><div class="wrap">`
    + `<h1>${esc(req.requestNo)}</h1>`
    + `<p class="sub">Client request summary · <span class="pill">${esc(req.status)}</span></p>`
    + '<h2>Request</h2>'
    + kv([
      ['Client', req.client],
      ['Project', req.project],
      ['Client contact', req.contact],
      ['Priority', req.priority],
      ['Date of call', req.calledOn ? fmtCalendarDay(req.calledOn) : ''],
      ['How they heard about us', req.heardAbout],
      ['Quote ID', req.quoteNo],
      ['Owner', req.owner],
    ])
    + '<h2>What was requested</h2>'
    + (htmlIsBlank(req.description)
      ? '<p class="na">Nothing recorded.</p>'
      : `<div class="desc">${safeRichText(req.description)}</div>`)
    + `<h2>Services · ${saved.length}</h2>${svcTable}${svcDetail}`
    + '<h2>History</h2>' + histTable
    + '<div class="foot">ALPACA Intelligence · Client Request · '
    // `nowClientWire()` — the viewer's own wall clock, not the UTC instant.
    // While the ADR-0008 kill-switch is on, `fmtLocal*` renders a value's
    // LITERAL wall-clock with no shift, so a `toISOString()` stamp prints the
    // UTC time: a summary produced at 10:21 was footed "04:51", which reads as
    // a document from five hours ago. A naive local value renders as itself.
    + `${esc(req.requestNo)} · produced ${esc(fmtLocalDateTime(nowClientWire()))}. `
    + 'A summary of the request record; it is not the Service Task Report, which is '
    + 'raised per service from the completed survey.</div>'
    + '</div></body></html>';
}

/** What happened, so the caller can say it in the shared notification service
 *  rather than this module reaching for one of its own (rule 7). */
export type SummaryOutcome = 'opened' | 'blocked';

/**
 * Build the summary for a SAVED request and open it for print.
 *
 * <p>The window is opened SYNCHRONOUSLY, before the reads: a `window.open` that
 * happens after an `await` is no longer attributable to the click and browsers
 * block it (the same reason `ModuleListView` opens first and navigates second).
 * It shows a "Preparing…" line until the document is ready, so the user is never
 * looking at a blank tab wondering whether it worked.</p>
 */
export async function openRequestSummary(
  requestId: string, req: RequestSummaryInput,
): Promise<SummaryOutcome> {
  const w = window.open('', `crsummary_${Date.now()}`,
    'width=1000,height=900,scrollbars=yes,resizable=yes');
  if (!w) return 'blocked';

  w.document.open();
  w.document.write(
    '<!doctype html><html><head><meta charset="utf-8">'
    + `<title>${esc(req.requestNo)} · Request summary</title></head>`
    + '<body style="margin:0;font-family:Tahoma,Verdana,\'Segoe UI\',sans-serif;'
    + 'font-size:12px;color:#6b7280;padding:24px">Preparing the summary…</body></html>');
  w.document.close();

  try {
    const [tasks, defs, audit] = await Promise.all([
      fetchServiceTasks(requestId),
      fetchServiceDefinitions(),
      // The history is a nice-to-have on a printout: if the audit read fails the
      // summary is still worth having, so it degrades to "nothing recorded"
      // rather than losing the whole document.
      fetchAuditTrail(AUDIT_FORM_NAME, requestId).catch(() => [] as AuditTrailRow[]),
    ]);

    w.document.open();
    w.document.write(buildRequestSummaryHtml(req, tasks, defs, audit));
    w.document.close();
    // The print dialog is raised after the document has settled; without the
    // small delay Chrome prints the page it was still parsing.
    w.setTimeout(() => { w.focus(); w.print(); }, 300);
    return 'opened';
  } catch (e) {
    // The window is already open and showing "Preparing…" — leaving it that way
    // would be the one outcome the user cannot interpret.
    w.close();
    throw e;
  }
}
